import {membership} from "../../lib/Membership.ts";
import {createWebsocket, deleteSessionCookie, sessionToCookie} from "../../lib/api.ts";
import { t } from "i18next";

const log_print = console.log;
const log_print_err = console.error;

export const TerminalStatus = {
    OK: t("terminal.status.ok"),
    ATTEMPTING_TO_CONNECT: t("terminal.status.connecting"),
    ACTIVE_CONSOLE_DISABLED: t("terminal.status.consoleDisabled"),
    WEBSOCKET_ONCLOSE: t("terminal.status.websocketClosed"),
    WEBSOCKET_ONERROR: t("terminal.status.websocketError"),
}


export interface WebSocketOnMessageData {
    type: string;
    index?: number;
    data?: string;
    col?: number;
    row?: number;
    req_key?: string;
    tab_key?: number;
    instance: Record<string, string>
}


export interface InstanceInfo {
    addresses: string[]
    image_name: string
    name: string
    status: string
}

export interface WebsocketTerminalEventHandler {
    onInit(): void;

    onInitTab(reqKey: string, tabKey: number): void;

    onChangeStatus(text: string, color?: string): void;

    onChangeTitle(tabKey: number, title: string): void;

    onRecvData(tabKey: number, data: string): void;

    onClose(tabKey: number): void;

    onIncorrectKeyPassword(): void;

    onTerminalNotPrepared(): void;

    onInstanceInfo(instanceInfo: InstanceInfo): void;

    onFailedAuthentication(): void;

    /** Blueshift Backend 환경 설정에서 지정한 웹 터미널 탭 제한수 초과 */
    onErrMaxTerminalSessionExceeded(reqKey: string): void

    /**
     * VM sshd의 제한 초과
     * `/etc/ssh/sshd_config`의 `MaxSessions` 으로 지정
     */
    onErrSshdMaxSessionExceeded(reqKey: string): void;

    /** floating ip가 붙지 않은 인스턴스 */
    onInstanceNetworkErr(): void;
}

interface WebSocketTerminalMessageDispatcher {
    init(): void;

    instance_info(webSocketMsg: WebSocketOnMessageData): void;

    stdout(webSocketMsg: WebSocketOnMessageData): void;

    tab_new(data: WebSocketOnMessageData): void;

    tab_closed(data: WebSocketOnMessageData): void;

    /** Blueshift Backend 환경 설정에서 지정한 웹 터미널 탭 제한수 초과 */
    err_max_terminal_session_exceeded(data: WebSocketOnMessageData): void;

    /**
     * VM sshd의 제한 초과
     * `/etc/ssh/sshd_config`의 `MaxSessions` 으로 지정
     */
    err_sshd_max_session_exceeded(data: WebSocketOnMessageData): void;

    err_key_password(): void;

    err_terminal_init_timeout(): void;

    err_not_start_sshd(): void;

    /** floating ip가 붙지 않은 인스턴스 */
    err_network(): void;
}

class WebSocketTerminalMessageDispatcherImpl implements WebSocketTerminalMessageDispatcher {
    private readonly webSocketTerminal: WebSocketTerminal;
    private readonly handler: WebsocketTerminalEventHandler;
    private retry_looper: number | undefined = undefined;

    constructor(webSocketTerminal: WebSocketTerminal, handler: WebsocketTerminalEventHandler) {
        this.webSocketTerminal = webSocketTerminal;
        this.handler = handler;
    }

    init() {
        this.handler.onInit();
    }

    instance_info(webSocketMsg: WebSocketOnMessageData) {
        this.handler.onInstanceInfo({
            addresses: webSocketMsg.instance['addresses'] as unknown as string[],
            image_name: webSocketMsg.instance['image_name'],
            name: webSocketMsg.instance['name'],
            status: webSocketMsg.instance['status']
        });
    }

    stdout(webSocketMsg: WebSocketOnMessageData) {
        const tab_key = webSocketMsg.tab_key!;
        const data = webSocketMsg.data!;

        setTimeout(() => {
            // eslint-disable-next-line no-control-regex
            const match = data!.match(/\x1B]0;.*\x07/);
            if (match) {
                // eslint-disable-next-line no-control-regex
                const end = match[0].match(/\x07/)!['index'];
                const title = match[0].substring(4, end);

                this.handler.onChangeTitle(tab_key!, title);
            }
        }, 50);
        this.handler.onRecvData(tab_key, data);
    }

    tab_new(data: WebSocketOnMessageData) {
        this.handler.onInitTab(data["req_key"]!, data["tab_key"]!);
    }

    tab_closed(data: WebSocketOnMessageData) {
        this.handler.onClose(data["tab_key"]!);
    }

    err_max_terminal_session_exceeded(data: WebSocketOnMessageData) {
        this.handler.onErrMaxTerminalSessionExceeded(data.req_key!);
    }

    err_sshd_max_session_exceeded(data: WebSocketOnMessageData) {
        this.handler.onErrSshdMaxSessionExceeded(data.req_key!);
    }

    err_key_password() {
        this.handler.onIncorrectKeyPassword();
    }

    err_terminal_init_timeout() {
        this.retryInit("err_terminal_init_timeout");
        this.handler.onTerminalNotPrepared();
    }

    err_not_start_sshd() {
        this.retryInit("err_terminal_init_timeout");
        this.handler.onTerminalNotPrepared();
    }

    err_network() {
        this.retryInit("err_network");
        this.handler.onInstanceNetworkErr();
    }

    private retryInit(cause: string) {
        console.warn(`retry cause ${cause}`);

        let retryCnt = 0;
        const retryTime = 30;

        window.clearInterval(this.retry_looper);
        this.retry_looper = window.setInterval(() => {
            this.handler.onChangeStatus(`${t("terminal.waitingForReconnection")} ${retryCnt++}/${retryTime}`, "#bf8b55");
        }, 1000);

        window.setTimeout(() => {
            window.clearInterval(this.retry_looper);
            this.webSocketTerminal.init();
        }, retryTime * 1000);
    }
}

export default class WebSocketTerminal {
    private readonly webSocket: WebSocket;
    private readonly dispatcher: WebSocketTerminalMessageDispatcherImpl;
    private handler: WebsocketTerminalEventHandler;

    constructor(instanceId: number, handler: WebsocketTerminalEventHandler) {
        this.handler = handler;
        this.dispatcher = new WebSocketTerminalMessageDispatcherImpl(this, handler);

        try {
            sessionToCookie();
            this.webSocket = createWebsocket(`terminal/${instanceId}`);
            log_print('createWebsocket');
        } catch (e) {
            log_print(e);
            throw e;
        }


        this.webSocket.onerror = e => {
            deleteSessionCookie();

            this.handler.onChangeStatus(TerminalStatus.WEBSOCKET_ONERROR, "#ff0000");
            log_print(e);
            log_print('WebSocket error: ', e);
        }

        this.webSocket.onopen = () => {
            deleteSessionCookie();

            if (!membership.session_id) {
                this.handler.onFailedAuthentication();
                return;
            }

            this.handler.onChangeStatus(TerminalStatus.ATTEMPTING_TO_CONNECT);
            this.init();
        };

        this.webSocket.onclose = () => {
            this.handler.onChangeStatus(TerminalStatus.WEBSOCKET_ONCLOSE, "#ff0000");
        };

        this.webSocket.onmessage = (e) => {
            this.doDispatch(e);
        };
    }

    private doDispatch(e: MessageEvent<string>) {
        const data = JSON.parse(e.data);

        if (data.type === 'ping') {
            this.sendMsg({'type': 'pong'});
            return;
        }

        try {
            this.dispatcher[data.type as keyof WebSocketTerminalMessageDispatcherImpl](data);
        } catch (ex) {
            if (ex instanceof SyntaxError) {
                log_print_err(e.data);
            } else if (ex instanceof TypeError) {
                log_print_err(`${data.type} not found in handler`, ex);
            } else {
                log_print_err(e);
                log_print_err(ex);
                throw ex;
            }
        }
    }

    init(password?: string) {
        this.sendMsg({'type': 'terminal_init', "password": password});
    }

    private sendMsg(message: object) {
        const webSocket = this.webSocket;

        if (webSocket.readyState === webSocket.CLOSED || webSocket.readyState === webSocket.CLOSING) {
            return;
        }

        webSocket.send(JSON.stringify(message));
    }

    newTab(reqKey: string) {
        this.sendMsg({"type": "tab_new", "req_key": reqKey,});
    }


    resize(cols: number, rows: number) {
        this.sendMsg({'type': 'resize', 'cols': cols, 'rows': rows,});
    }

    closeTab(tab_key: number) {
        this.sendMsg({'type': 'tab_close', 'tab_key': tab_key,})
    }

    send(index: number, input_data: string) {
        this.sendMsg({'type': 'stdin', 'tab_key': index, 'data': input_data,});
    }

    /**
     * 터미널을 찾을 닫는 경우 웹 소켓이 닫힌다
     */
    close() {
        this.webSocket.close();
    }
}
